import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'react-feather';
import { ModalButtonBuilder, ButtonBuilder, ApiRequest, ToastBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import CompositeValueSelect from '../composite/CompositeValueSelect';
import { INACTIVE_STATUSES } from '../../shared/mappingConflicts';

/**
 * Map By Leaf — map TSI leaves to a single destination composite.
 *
 * A "leaf" is one node at one level (type / subtype / item). What makes two association rows the SAME leaf at a level is
 * configurable per source resource via source_chain.leaf_identity: a map of level -> the explicit list of level names
 * whose values together form the leaf's key. Everything not in that list (and board) is context that can differ. The
 * default (MedicusIT/CWM) treats subtype "Server" as one leaf across its parent types, and item "4 - Citrix" as one leaf
 * keyed by (item, subtype):
 *   { type: ['type'], subtype: ['subtype'], item: ['item', 'subtype'] }
 *
 * Selecting a leaf maps ONLY that level's own rows (no cascade): a subtype leaf touches the bare (type, subtype, 0)
 * associations, not the items beneath them. Boards are expand-only context and never mappable.
 */

const DEFAULT_LEAF_IDENTITY = { type: ['type'], subtype: ['subtype'], item: ['item', 'subtype'] };
const LEVELS = ['type', 'subtype', 'item'];
const LEVEL_LABEL = { type: 'Types', subtype: 'SubTypes', item: 'Items' };
const nameField = (level) => `${level}_name`;

// Rows that belong to a level (no cascade): the bare row for that level. A type row has no subtype/item name; a subtype
// row has a subtype name but no item name; an item row has an item name.
const levelRows = (level, rows) => {
    if (level === 'type') return rows.filter(r => r.type_name && !r.subtype_name && !r.item_name);
    if (level === 'subtype') return rows.filter(r => r.subtype_name && !r.item_name);
    return rows.filter(r => r.item_name);
};

const boardCount = (rows) => new Set(rows.map(r => r.board_name)).size;
const typeCount = (rows) => new Set(rows.map(r => r.type_name)).size;

const destLabel = (rows) => {
    const mapped = rows.filter(r => r.destination_id > 0 && !INACTIVE_STATUSES.includes(r.status));
    const distinct = [...new Set(mapped.map(r => r.destination_path || r.destination_value))];
    if (distinct.length === 0) return null;
    if (distinct.length === 1) return distinct[0];
    return `${distinct.length} different destinations`;
};

// Group a level's rows into leaves keyed by its configured identity fields. Returns [{ id, level, name, identity, rows }].
const buildLeaves = (level, rows, identityFields) => {
    const byKey = new Map();
    for (const r of levelRows(level, rows)) {
        const key = identityFields.map(f => r[nameField(f)] ?? '').join(' ∷ ');
        if (!byKey.has(key)) {
            const identity = {};
            identityFields.forEach(f => { identity[nameField(f)] = r[nameField(f)]; });
            byKey.set(key, { id: `${level}:${key}`, level, name: r[nameField(level)], identity, rows: [] });
        }
        byKey.get(key).rows.push(r);
    }
    return [...byKey.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

// Context (non-identity) parent names for a leaf's label, e.g. an item keyed by (item, subtype) shows "under <subtype>".
const contextLabel = (leaf) => {
    const parents = Object.entries(leaf.identity)
        .filter(([f]) => f !== nameField(leaf.level))
        .map(([, v]) => v)
        .filter(Boolean);
    return parents.length ? `under ${parents.join(' / ')}` : null;
};

const MapByLeafModal = ({ jobId, sourceResourceId, rows, destChainData, leafIdentity, onDone }) => {
    const [expandedSections, setExpandedSections] = useState(new Set(LEVELS));
    const [expandedLeaves, setExpandedLeaves] = useState(new Set());
    const [selected, setSelected] = useState(new Set());
    const [search, setSearch] = useState('');
    const [destinationId, setDestinationId] = useState(null);
    const [status, setStatus] = useState('Pending Approval');
    const [saving, setSaving] = useState(false);

    const identity = useMemo(() => {
        const cfg = leafIdentity && typeof leafIdentity === 'object' ? leafIdentity : {};
        const resolved = {};
        for (const lvl of LEVELS) {
            const fields = Array.isArray(cfg[lvl]) && cfg[lvl].length ? cfg[lvl] : DEFAULT_LEAF_IDENTITY[lvl];
            resolved[lvl] = fields.filter(f => LEVELS.includes(f));
        }
        return resolved;
    }, [leafIdentity]);

    // One de-duplicated leaf list per level, keyed by the configured identity.
    const leavesByLevel = useMemo(() => {
        const out = {};
        for (const lvl of LEVELS) out[lvl] = buildLeaves(lvl, rows || [], identity[lvl]);
        return out;
    }, [rows, identity]);

    // Complete id -> leaf index so selections survive collapse/search.
    const leafIndex = useMemo(() => {
        const idx = {};
        for (const lvl of LEVELS) for (const leaf of leavesByLevel[lvl]) idx[leaf.id] = leaf;
        return idx;
    }, [leavesByLevel]);

    const toggleSection = (lvl) => setExpandedSections(prev => { const n = new Set(prev); n.has(lvl) ? n.delete(lvl) : n.add(lvl); return n; });
    const toggleLeafExpand = (id) => setExpandedLeaves(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const q = search.trim().toLowerCase();
    const matchesLeaf = (leaf) => {
        if (!q) return true;
        if ((leaf.name || '').toLowerCase().includes(q)) return true;
        return Object.values(leaf.identity).some(v => (v || '').toLowerCase().includes(q));
    };

    const selectedLeaves = [...selected].map(id => leafIndex[id]).filter(Boolean);

    const apply = async () => {
        if (!selected.size) return ToastBuilder('error', 'Select at least one leaf');
        if (!destinationId || Number(destinationId) <= 0) return ToastBuilder('error', 'Pick a destination');

        // Send the level + the leaf's identity names; the backend matches associations on exactly these (no cascade).
        const leaves = selectedLeaves.map(l => ({ level: l.level, ...l.identity }));

        setSaving(true);
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/compositeMappings', {
            pathParams: { jobId },
            body: { action: 'mapByLeaf', leaves, destination_id: parseInt(destinationId), status },
            toastOnError: true,
        });
        setSaving(false);

        if (res.success && res.data?.success !== false) {
            const upserted = res.data?.upserted || 0;
            const matched = res.data?.matched_leaves || 0;
            if (upserted > 0) {
                ToastBuilder('success', `Mapped ${matched} leaf(s) across ${upserted} association(s)`);
                setSelected(new Set());
                setDestinationId(null);
                if (onDone) onDone();
            } else {
                ToastBuilder('error', 'No associations matched the selected leaves — nothing was mapped');
            }
        } else {
            ToastBuilder('error', res.data?.message || res.error || 'Failed to map leaves');
        }
    };

    const renderLeafRow = (leaf) => {
        const boards = boardCount(leaf.rows);
        const types = typeCount(leaf.rows);
        const dest = destLabel(leaf.rows);
        const ctx = contextLabel(leaf);
        const isExpanded = expandedLeaves.has(leaf.id);
        const boardNames = [...new Set(leaf.rows.map(r => r.board_name).filter(Boolean))].sort();
        return (
            <React.Fragment key={leaf.id}>
                <div className='d-flex align-items-center px-2 py-1' style={{ paddingLeft: 30, borderBottom: '1px solid #f1f3f5', fontSize: 13 }}>
                    <span style={{ width: 18, cursor: 'pointer' }} onClick={() => toggleLeafExpand(leaf.id)}>
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </span>
                    <input type='checkbox' className='form-check-input mt-0 me-2' checked={selected.has(leaf.id)} onChange={() => toggleSelect(leaf.id)} />
                    <span style={{ flex: 1 }}>
                        {leaf.name}
                        {ctx && <span className='text-muted ms-1' style={{ fontSize: 11 }}>{ctx}</span>}
                        <span className='badge badge-light ms-1' title='Boards this leaf spans'>{boards} board{boards !== 1 ? 's' : ''}</span>
                        {types > 1 && <span className='badge badge-light ms-1' title='Types this leaf spans'>{types} types</span>}
                    </span>
                    {dest
                        ? <span className='text-muted' style={{ fontSize: 11, maxWidth: 220, textAlign: 'right' }}>→ {dest}</span>
                        : <span className='txt-warning' style={{ fontSize: 11 }}>unmapped</span>}
                </div>
                {isExpanded && boardNames.map(b => (
                    <div key={`${leaf.id}|board:${b}`} className='d-flex align-items-center px-2 py-1 text-muted' style={{ paddingLeft: 70, borderBottom: '1px solid #f8f9fa', fontSize: 11, fontStyle: 'italic' }}>
                        {b}
                    </div>
                ))}
            </React.Fragment>
        );
    };

    return (
        <ModalButtonBuilder
            title='Map by Leaf'
            className='btn-outline-primary m-1 py-2'
            modalTitle='Map TSI Leaves to a Destination'
            style={{ maxWidth: 1225, width: '95vw' }}
            onSubmit={false}
            onCancelLabel='Close'
            onClick={() => { setSelected(new Set()); setDestinationId(null); setSearch(''); setExpandedSections(new Set(LEVELS)); setExpandedLeaves(new Set()); }}
            body={
                <div>
                    <p className='text-muted' style={{ fontSize: 13 }}>
                        Pick one or more leaves and one destination. A leaf is mapped only at its own level — selecting a subtype does not
                        touch the items under it. What counts as one leaf (e.g. whether a subtype is shared across its parent types) follows
                        this resource's leaf-identity config. Different leaves mapped to the same destination may overload it; the composite table flags that.
                    </p>
                    <input
                        className='form-control form-control-sm mb-2'
                        placeholder='Search leaves...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ flex: '1 1 auto', minWidth: 0, maxHeight: 440, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: 6 }}>
                            {LEVELS.map(lvl => {
                                const leaves = (leavesByLevel[lvl] || []).filter(matchesLeaf);
                                const open = expandedSections.has(lvl);
                                return (
                                    <div key={lvl}>
                                        <div className='d-flex align-items-center px-2 py-1 section-card-header' style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }} onClick={() => toggleSection(lvl)}>
                                            <span style={{ width: 18 }}>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
                                            {LEVEL_LABEL[lvl]}
                                            <span className='badge badge-light ms-2'>{leaves.length}</span>
                                        </div>
                                        {open && (leaves.length ? leaves.map(renderLeafRow) : <div className='px-3 py-2 text-muted' style={{ fontSize: 12 }}>No {LEVEL_LABEL[lvl].toLowerCase()}.</div>)}
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ flex: '0 0 calc(40% + 25px)' }}>
                            <label className='form-label fw-bold' style={{ fontSize: 13 }}>Destination</label>
                            <CompositeValueSelect
                                jobId={jobId}
                                sourceResourceId={sourceResourceId}
                                value={destinationId}
                                onChange={setDestinationId}
                                chainData={destChainData}
                                defaultMode='flat'
                                compact
                            />
                            <small className='text-muted d-block mt-1'>Pick at any level to match the source level (type → category, subtype → subcategory, item → item).</small>

                            <div className='mt-3'>
                                <label className='form-label fw-bold' style={{ fontSize: 13 }}>Status</label>
                                <select className='form-select form-select-sm' value={status} onChange={(e) => setStatus(e.target.value)}>
                                    {['Pending Approval', 'Approved', 'Under Review', 'Dev Review'].map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>

                            <div className='mt-3 p-2' style={{ border: '1px solid #e9ecef', borderRadius: 6, background: '#f8f9fa', fontSize: 13 }}>
                                <div className='fw-bold mb-1'>{selected.size} leaf(s) selected</div>
                                {selected.size > 1 && (
                                    <div className='txt-warning mb-1' style={{ fontSize: 12 }}>
                                        ⚠ Mapping multiple leaves to one destination overloads it wherever they share a context.
                                    </div>
                                )}
                                <ul className='mb-0' style={{ paddingLeft: 18, maxHeight: 140, overflowY: 'auto' }}>
                                    {selectedLeaves.map((l) => {
                                        const ctx = contextLabel(l);
                                        return <li key={l.id}>{l.name} <span className='text-muted'>({l.level}{ctx ? ` ${ctx}` : ''})</span></li>;
                                    })}
                                </ul>
                            </div>

                            <ButtonBuilder
                                label={saving ? 'Mapping...' : `Map ${selected.size} leaf(s)`}
                                className='btn-primary mt-3'
                                type='button'
                                disabled={saving || !selected.size || !destinationId}
                                onClick={apply}
                            />
                        </div>
                    </div>
                </div>
            }
        />
    );
};

export default MapByLeafModal;
